import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { QRCodeCanvas } from 'qrcode.react';
import { Plus, Trash2, Download, Calendar, Users, Zap, TrendingUp, MapPin } from 'lucide-react';
import StationBuilder from './StationBuilder';

const EventsDashboard = ({ user }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEventForm, setShowEventForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedEventForEdit, setSelectedEventForEdit] = useState(null);
  const [showStationBuilder, setShowStationBuilder] = useState(false);
  const [selectedEventForStations, setSelectedEventForStations] = useState(null);
  const [copiedLink, setCopiedLink] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    recipient_name: '',
    recipient_id: '',
    release_date: '',
    release_time: ''
  });
  const [editFormData, setEditFormData] = useState({
    name: '',
    description: '',
    recipient_name: '',
    release_date: '',
    release_time: ''
  });

  useEffect(() => {
    if (user?.email) {
      loadEvents();
    }
  }, [user]);

  const loadEvents = async () => {
    try {
      setLoading(true);
      const response = await axios.get('http://localhost:4500/api/events/list', {
        params: { email: user.email }
      });
      setEvents(response.data.events || []);
      console.log('✅ Events loaded:', response.data.events);
    } catch (error) {
      console.error('❌ Error loading events:', error);
      alert('Fehler beim Laden der Events');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateEvent = async (e) => {
    e.preventDefault();
    if (!formData.name) {
      alert('Event-Name erforderlich');
      return;
    }

    try {
      const response = await axios.post('http://localhost:4500/api/events/create', {
        email: user.email,
        name: formData.name,
        description: formData.description,
        recipient_name: formData.recipient_name,
        recipient_id: formData.recipient_id || null,
        release_date: formData.release_date || null,
        release_time: formData.release_time || null
      });

      console.log('✅ Event created:', response.data);
      setFormData({ name: '', description: '', recipient_name: '', recipient_id: '', release_date: '', release_time: '' });
      setShowEventForm(false);
      alert('✅ Event erfolgreich erstellt!');
      loadEvents();
    } catch (error) {
      console.error('❌ Error creating event:', error);
      alert('Fehler beim Erstellen des Events');
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (window.confirm('Event wirklich löschen?')) {
      try {
        await axios.delete(`http://localhost:4500/api/events/${eventId}`, {
          params: { email: user.email }
        });
        setEvents(events.filter(e => e.id !== eventId));
        alert('✅ Event gelöscht!');
      } catch (error) {
        console.error('❌ Error deleting event:', error);
        alert('Fehler beim Löschen des Events');
      }
    }
  };

  const handleStartEdit = (event) => {
    setSelectedEventForEdit(event);
    setEditFormData({
      name: event.name,
      description: event.description,
      recipient_name: event.recipient_name || '',
      release_date: event.release_date || '',
      release_time: event.release_time || ''
    });
    setShowEditForm(true);
  };

  const handleEditEvent = async (e) => {
    e.preventDefault();
    if (!editFormData.name) {
      alert('Event-Name erforderlich');
      return;
    }

    try {
      await axios.put(`http://localhost:4500/api/events/${selectedEventForEdit.id}`, {
        email: user.email,
        name: editFormData.name,
        description: editFormData.description,
        recipient_name: editFormData.recipient_name,
        release_date: editFormData.release_date || null,
        release_time: editFormData.release_time || null
      });

      console.log('✅ Event updated');
      setShowEditForm(false);
      alert('✅ Event erfolgreich aktualisiert!');
      loadEvents();
    } catch (error) {
      console.error('❌ Error updating event:', error);
      alert('Fehler beim Aktualisieren des Events');
    }
  };

  const downloadEventQR = (event) => {
    const qrRef = document.getElementById(`qr-${event.id}`);
    if (qrRef) {
      const canvas = qrRef.querySelector('canvas');
      const url = canvas.toDataURL('image/png');
      const link = document.createElement('a');
      link.href = url;
      link.download = `${event.name}-QR.png`;
      link.click();
    }
  };

  const copyEventLink = (event) => {
    const link = `${window.location.origin}/event/${event.event_qr_token}`;
    navigator.clipboard.writeText(link).then(() => {
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 2000);
    });
  };

  // Calculate stats
  const totalEvents = events.length;
  const totalStations = events.reduce((sum, e) => sum + (e.station_count || 0), 0);
  const activeEvents = events.length;

  // Get color for event card
  const getCardColor = (index) => {
    const colors = [
      'from-purple-600 to-purple-700',
      'from-blue-600 to-blue-700',
      'from-pink-600 to-pink-700',
      'from-orange-600 to-orange-700',
      'from-teal-600 to-teal-700',
    ];
    return colors[index % colors.length];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="text-center">
          <div className="inline-block animate-spin mb-4">
            <div className="w-12 h-12 border-4 border-purple-500 border-t-transparent rounded-full"></div>
          </div>
          <p className="text-slate-300">⏳ Events werden geladen...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-800 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header Section */}
        <div className="mb-8">
          <div className="flex justify-between items-start mb-6">
            <div>
              <h1 className="text-5xl font-bold text-white mb-2">📍 Schnitzeljagden</h1>
              <p className="text-slate-400 text-lg">Erstelle unvergessliche Erlebnisse für deine Hinterbliebenen</p>
            </div>
            <button
              onClick={() => setShowEventForm(!showEventForm)}
              className="flex items-center gap-2 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-bold py-3 px-6 rounded-xl transition-all shadow-lg hover:shadow-xl transform hover:scale-105"
            >
              <Plus size={24} /> Neue Schnitzeljagd
            </button>
          </div>

          {/* Create Event Form */}
          {showEventForm && (
            <div className="bg-slate-800/50 backdrop-blur-md border border-purple-500/30 rounded-2xl p-8 mb-8">
              <h2 className="text-2xl font-bold text-white mb-6">Neue Schnitzeljagd erstellen</h2>
              <form onSubmit={handleCreateEvent} className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="md:col-span-2">
                  <label className="block text-slate-300 mb-2 font-semibold">Event-Name *</label>
                  <input
                    type="text"
                    placeholder="z.B. Roadtrip Hamburg, Geburtstagsjagd"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-700 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 border border-slate-600"
                    required
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-slate-300 mb-2 font-semibold">Beschreibung</label>
                  <textarea
                    placeholder="Beschreibe das Event und seine Geschichte..."
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-700 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 border border-slate-600"
                    rows="3"
                  />
                </div>

                <div>
                  <label className="block text-slate-300 mb-2 font-semibold">Für wen? *</label>
                  <input
                    type="text"
                    placeholder="z.B. Max, Mein Sohn, Maria"
                    value={formData.recipient_name}
                    onChange={(e) => setFormData({ ...formData, recipient_name: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-700 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 border border-slate-600"
                  />
                </div>

                <div>
                  <label className="block text-slate-300 mb-2 font-semibold">Empfänger-ID (optional)</label>
                  <input
                    type="text"
                    placeholder="Empfänger ID..."
                    value={formData.recipient_id}
                    onChange={(e) => setFormData({ ...formData, recipient_id: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-700 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 border border-slate-600"
                  />
                </div>

                <div>
                  <label className="block text-slate-300 mb-2 font-semibold">Freigabedatum (optional)</label>
                  <input
                    type="date"
                    value={formData.release_date}
                    onChange={(e) => setFormData({ ...formData, release_date: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-700 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 border border-slate-600"
                  />
                </div>

                <div>
                  <label className="block text-slate-300 mb-2 font-semibold">Freigabeuhrzeit (optional)</label>
                  <input
                    type="time"
                    value={formData.release_time}
                    onChange={(e) => setFormData({ ...formData, release_time: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-700 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 border border-slate-600"
                  />
                </div>

                <div className="md:col-span-2 flex gap-4">
                  <button
                    type="submit"
                    className="flex-1 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-bold py-3 rounded-lg transition-all"
                  >
                    Schnitzeljagd erstellen
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowEventForm(false);
                      setFormData({ name: '', description: '', recipient_name: '', recipient_id: '' });
                    }}
                    className="flex-1 bg-slate-700 hover:bg-slate-600 text-white font-bold py-3 rounded-lg transition-all"
                  >
                    Abbrechen
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>

        {/* Main Grid: Events Cards + Stats */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          {/* Events Cards - Left Side (Takes 2 columns on large screens) */}
          <div className="lg:col-span-2">
            {events.length === 0 ? (
              <div className="bg-slate-800/50 backdrop-blur-md border-2 border-dashed border-purple-500/30 rounded-2xl p-12 text-center">
                <Calendar size={48} className="mx-auto mb-4 text-slate-400" />
                <p className="text-slate-400 text-lg mb-2">Noch keine Schnitzeljagden erstellt.</p>
                <p className="text-slate-500">Starten Sie eine neue Schnitzeljagd um unvergessliche Momente zu bewahren!</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {events.map((event, index) => (
                  <div
                    key={event.id}
                    onClick={() => setSelectedEvent(event)}
                    className={`relative overflow-hidden rounded-2xl p-6 text-white cursor-pointer transform transition-all hover:scale-105 hover:shadow-2xl bg-gradient-to-br ${getCardColor(index)} group`}
                  >
                    {/* Decorative elements */}
                    <div className="absolute top-0 right-0 w-40 h-40 bg-white/10 rounded-full -mr-20 -mt-20 group-hover:scale-110 transition-transform"></div>
                    <div className="absolute bottom-0 left-0 w-32 h-32 bg-white/5 rounded-full -ml-16 -mb-16"></div>

                    {/* Card Content */}
                    <div className="relative z-10">
                      <h3 className="text-2xl font-bold mb-2">{event.name}</h3>
                      {event.recipient_name && (
                        <p className="text-sm text-white/80 mb-3">👤 für <strong>{event.recipient_name}</strong></p>
                      )}
                      <p className="text-sm text-white/70 mb-4 line-clamp-2">{event.description}</p>

                      {/* Stats */}
                      <div className="flex items-center gap-4 mb-4 text-sm">
                        <div className="flex items-center gap-1 bg-white/20 px-3 py-1 rounded-full">
                          <Zap size={14} /> {event.station_count} Stationen
                        </div>
                        <div className="flex items-center gap-1 bg-white/20 px-3 py-1 rounded-full">
                          <Calendar size={14} /> {new Date(event.created_at).toLocaleDateString('de-DE')}
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div className="flex gap-2 flex-wrap">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            downloadEventQR(event);
                          }}
                          className="flex-1 bg-white/20 hover:bg-white/30 px-3 py-2 rounded-lg text-xs font-semibold transition-all"
                          title="QR-Code herunterladen"
                        >
                          📥 QR
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedEventForStations(event);
                            setShowStationBuilder(true);
                          }}
                          className="flex-1 bg-blue-600/50 hover:bg-blue-700 px-3 py-2 rounded-lg text-xs font-semibold transition-all"
                          title="Stationen verwalten"
                        >
                          🗺️ Stationen
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleStartEdit(event);
                          }}
                          className="flex-1 bg-yellow-600/50 hover:bg-yellow-700 px-3 py-2 rounded-lg text-xs font-semibold transition-all"
                          title="Event bearbeiten"
                        >
                          ✏️ Bearbeiten
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteEvent(event.id);
                          }}
                          className="flex-1 bg-red-600/50 hover:bg-red-700 px-3 py-2 rounded-lg text-xs font-semibold transition-all"
                          title="Event löschen"
                        >
                          🗑️ Löschen
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Statistics Panel - Right Side */}
          <div className="space-y-6">
            {/* Stats Cards */}
            <div className="bg-slate-800/50 backdrop-blur-md border border-purple-500/30 rounded-2xl p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="text-slate-400 text-sm">Gesamt Schnitzeljagden</p>
                  <p className="text-4xl font-bold text-white">{totalEvents}</p>
                </div>
                <div className="w-14 h-14 bg-gradient-to-br from-purple-600 to-pink-600 rounded-xl flex items-center justify-center text-2xl">
                  📍
                </div>
              </div>
              <div className="w-full bg-slate-700 rounded-full h-2">
                <div className="bg-gradient-to-r from-purple-600 to-pink-600 h-2 rounded-full" style={{ width: '100%' }}></div>
              </div>
            </div>

            <div className="bg-slate-800/50 backdrop-blur-md border border-blue-500/30 rounded-2xl p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-sm">Stationen gesamt</p>
                  <p className="text-4xl font-bold text-white">{totalStations}</p>
                </div>
                <div className="w-14 h-14 bg-gradient-to-br from-blue-600 to-cyan-600 rounded-xl flex items-center justify-center text-2xl">
                  🎯
                </div>
              </div>
            </div>

            <div className="bg-slate-800/50 backdrop-blur-md border border-green-500/30 rounded-2xl p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-sm">Aktiv heute</p>
                  <p className="text-4xl font-bold text-white">{activeEvents}</p>
                </div>
                <div className="w-14 h-14 bg-gradient-to-br from-green-600 to-emerald-600 rounded-xl flex items-center justify-center text-2xl">
                  ✨
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-gradient-to-br from-slate-800 to-slate-900 backdrop-blur-md border border-slate-700 rounded-2xl p-6">
              <h3 className="text-white font-bold mb-4 flex items-center gap-2">
                <TrendingUp size={18} /> Schnellzugriff
              </h3>
              <div className="space-y-2">
                {events.slice(0, 3).map((event) => (
                  <button
                    key={event.id}
                    onClick={() => setSelectedEvent(event)}
                    className="w-full text-left px-4 py-2 bg-slate-700/50 hover:bg-slate-600 rounded-lg text-sm text-slate-300 transition-all"
                  >
                    <span className="font-semibold">{event.name}</span>
                    <span className="block text-xs text-slate-400">→ {event.station_count} Stationen</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Recent Events Section */}
        {events.length > 0 && (
          <div className="bg-slate-800/50 backdrop-blur-md border border-slate-700 rounded-2xl p-6">
            <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
              <Calendar size={24} /> Alle Schnitzeljagden
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-700">
                    <th className="text-left py-3 px-4 text-slate-400">Name</th>
                    <th className="text-left py-3 px-4 text-slate-400">Für</th>
                    <th className="text-left py-3 px-4 text-slate-400">Stationen</th>
                    <th className="text-left py-3 px-4 text-slate-400">Erstellt</th>
                    <th className="text-right py-3 px-4 text-slate-400">Aktionen</th>
                  </tr>
                </thead>
                <tbody>
                  {events.map((event) => (
                    <tr key={event.id} className="border-b border-slate-700/50 hover:bg-slate-700/30 transition-all cursor-pointer" onClick={() => setSelectedEvent(event)}>
                      <td className="py-3 px-4 text-white font-semibold">{event.name}</td>
                      <td className="py-3 px-4 text-slate-300">{event.recipient_name || '—'}</td>
                      <td className="py-3 px-4 text-slate-300">{event.station_count}</td>
                      <td className="py-3 px-4 text-slate-400 text-xs">{new Date(event.created_at).toLocaleDateString('de-DE')}</td>
                      <td className="py-3 px-4 text-right">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            copyEventLink(event);
                          }}
                          className="text-xs bg-purple-600/50 hover:bg-purple-600 px-3 py-1 rounded transition-all"
                          title="Link kopieren"
                        >
                          {copiedLink ? '✓' : '📋'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Event Detail Modal */}
      {selectedEvent && (
        <div
          className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 z-50"
          onClick={() => {
            setSelectedEvent(null);
            setCopiedLink(false);
          }}
        >
          <div
            className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl p-8 max-w-2xl w-full border border-slate-700 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-3xl font-bold text-white mb-2">{selectedEvent.name}</h2>
            {selectedEvent.recipient_name && (
              <p className="text-purple-400 mb-4 text-lg">📍 für <strong>{selectedEvent.recipient_name}</strong></p>
            )}
            <p className="text-slate-300 mb-6">{selectedEvent.description}</p>

            {/* Link Section */}
            <div className="mb-6 bg-slate-700/50 p-4 rounded-xl border border-slate-600">
              <p className="text-slate-300 mb-2 text-sm font-semibold">🔗 Schnitzeljagd-Link:</p>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  readOnly
                  value={`${window.location.origin}/event/${selectedEvent.event_qr_token}`}
                  className="flex-1 px-3 py-2 bg-slate-600 text-slate-300 text-sm rounded border border-slate-500 focus:outline-none"
                />
                <button
                  onClick={() => copyEventLink(selectedEvent)}
                  className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white text-sm font-bold rounded transition-all"
                >
                  {copiedLink ? '✓ Kopiert!' : '📋 Kopieren'}
                </button>
              </div>
            </div>

            {/* QR Code */}
            <div className="mb-6">
              <p className="text-slate-300 mb-3 font-semibold">📱 QR-Code für Empfänger:</p>
              <div className="flex justify-center bg-white p-4 rounded-xl mb-2">
                <QRCodeCanvas
                  value={`${window.location.origin}/event/${selectedEvent.event_qr_token}`}
                  size={220}
                  level="H"
                  includeMargin={true}
                />
              </div>
              <p className="text-xs text-slate-400 text-center">QR-Code scannen = Schnitzeljagd starten</p>
            </div>

            {/* Buttons */}
            <div className="flex gap-4">
              <button
                onClick={() => downloadEventQR(selectedEvent)}
                className="flex-1 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-bold py-3 rounded-lg transition-all"
              >
                📥 QR-Code herunterladen
              </button>
              <button
                onClick={() => {
                  setSelectedEvent(null);
                  setCopiedLink(false);
                }}
                className="flex-1 bg-slate-700 hover:bg-slate-600 text-white font-bold py-3 rounded-lg transition-all"
              >
                Schließen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Station Builder Modal */}
      {showStationBuilder && selectedEventForStations && (
        <StationBuilder
          eventId={selectedEventForStations.id}
          userEmail={user.email}
          onClose={() => {
            setShowStationBuilder(false);
            setSelectedEventForStations(null);
            loadEvents(); // Refresh events to show updated station counts
          }}
        />
      )}

      {/* Edit Event Modal */}
      {showEditForm && selectedEventForEdit && (
        <div
          className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 z-50"
          onClick={() => setShowEditForm(false)}
        >
          <div
            className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl p-8 max-w-2xl w-full border border-slate-700 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-3xl font-bold text-white mb-6">Event bearbeiten: {selectedEventForEdit.name}</h2>
            
            <form onSubmit={handleEditEvent} className="space-y-6">
              <div>
                <label className="block text-slate-300 mb-2 font-semibold">Event-Name *</label>
                <input
                  type="text"
                  placeholder="z.B. Roadtrip Hamburg"
                  value={editFormData.name}
                  onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                  className="w-full px-4 py-3 bg-slate-700 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 border border-slate-600"
                  required
                />
              </div>

              <div>
                <label className="block text-slate-300 mb-2 font-semibold">Beschreibung</label>
                <textarea
                  placeholder="Beschreibe das Event und seine Geschichte..."
                  value={editFormData.description}
                  onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
                  className="w-full px-4 py-3 bg-slate-700 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 border border-slate-600"
                  rows="4"
                />
              </div>

              <div>
                <label className="block text-slate-300 mb-2 font-semibold">Für wen?</label>
                <input
                  type="text"
                  placeholder="z.B. Max, Mein Sohn, Maria"
                  value={editFormData.recipient_name}
                  onChange={(e) => setEditFormData({ ...editFormData, recipient_name: e.target.value })}
                  className="w-full px-4 py-3 bg-slate-700 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 border border-slate-600"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-slate-300 mb-2 font-semibold">Freigabedatum</label>
                  <input
                    type="date"
                    value={editFormData.release_date}
                    onChange={(e) => setEditFormData({ ...editFormData, release_date: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-700 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 border border-slate-600"
                  />
                </div>
                <div>
                  <label className="block text-slate-300 mb-2 font-semibold">Freigabeuhrzeit</label>
                  <input
                    type="time"
                    value={editFormData.release_time}
                    onChange={(e) => setEditFormData({ ...editFormData, release_time: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-700 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 border border-slate-600"
                  />
                </div>
              </div>

              <div className="flex gap-4">
                <button
                  type="submit"
                  className="flex-1 bg-gradient-to-r from-yellow-600 to-yellow-700 hover:from-yellow-700 hover:to-yellow-800 text-white font-bold py-3 rounded-lg transition-all"
                >
                  ✅ Speichern
                </button>
                <button
                  type="button"
                  onClick={() => setShowEditForm(false)}
                  className="flex-1 bg-slate-700 hover:bg-slate-600 text-white font-bold py-3 rounded-lg transition-all"
                >
                  Abbrechen
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      )}
    </div>
  );
};

export default EventsDashboard;
